import { Link } from "@inertiajs/react";
import { Mail } from "lucide-react";
import Steps, { Step } from "@/components/help/Steps";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import TakeHomeSheetPreview from "@/components/schools/TakeHomeSheetPreview";
import ActivationCard from "@/components/parents/ActivationCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SCHOOL, CAMPAIGN_ACTIVE } from "@/help/fixtures";

export default function ActivatingYourChild() {
  return (
    <>
      <p>
        Your child came home with a take-home sheet. To follow their reading and let family donate, sign up using the sheet — it takes about a minute.
      </p>

      <p>
        You'll need the take-home sheet (or just the code from it) and your own email address.
      </p>

      <h2>What the sheet looks like</h2>
      <Screenshot caption="The take-home sheet" padded={false}>
        <div className="p-6 bg-secondary/30">
          <TakeHomeSheetPreview
            school={SCHOOL}
            campaign={CAMPAIGN_ACTIVE}
            student={{
              full_name: "Sarah T.",
              first_name: "Sarah",
              classroom_name: "Room 12",
              grade: "Grade 5",
              claim_code: "BEAR-MOON-4217",
            }}
          />
        </div>
      </Screenshot>
      <p>
        The big QR code on it takes you straight to the sign-up page. If your camera can't read it, you can also go to pagepledge.com/activate and type the code in by hand.
      </p>

      <h2>Step by step</h2>
      <Steps>
        <Step title="Scan the QR code">
          <p>
            Open your phone's camera, point it at the QR, tap the link. No app to install — it's just a webpage.
          </p>
        </Step>
        <Step title="Make sure it's the right child">
          <p>
            The page will show your child's first name, last initial, classroom, and school. If that's not your child, hold off and ask the teacher.
          </p>
          <Screenshot
            caption="The sign-up page"
            browser="pagepledge.com/activate?code=BEAR-MOON-4217"
            padded={false}
          >
            <div className="p-4 max-w-sm mx-auto">
              <ActivationCard claim_code="BEAR-MOON-4217" email="" variant="public" />
            </div>
          </Screenshot>
        </Step>
        <Step title="Enter your email">
          <p>
            We'll send you a sign-in link. There's no password to make.
          </p>
        </Step>
        <Step title="Click the link in your inbox">
          <p>
            The email comes through within seconds. Click the button to finish. The link works for 24 hours.
          </p>
          <Screenshot caption="The confirmation email" padded={false}>
            <div className="p-4 max-w-sm mx-auto">
              <Card>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Mail className="h-4 w-4" /> <strong className="text-foreground">PagePledge</strong> — Confirm your email
                  </div>
                  <p className="font-display text-base font-semibold text-foreground">Confirm your email</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Click below to finish setting up Sarah's account.
                  </p>
                  <Button>Confirm and view Sarah's page</Button>
                </CardContent>
              </Card>
            </div>
          </Screenshot>
        </Step>
        <Step title="You're in">
          <p>
            You'll land on your dashboard with your child's reading page right there. From here you can <Link href="/help/articles/sharing-with-family">share their donation page</Link>, <Link href="/help/articles/logging-minutes-at-home">log reading from home</Link>, and <Link href="/help/articles/setting-personal-goals">set a personal goal</Link>.
          </p>
        </Step>
      </Steps>

      <Callout variant="note" title="Already have a child on PagePledge?">
        <p>
          You don't need to sign up again — your same account can hold all your children. See <Link href="/help/articles/multiple-children">Adding more than one child</Link>.
        </p>
      </Callout>

      <h2>If something goes wrong</h2>
      <p>
        If the page says "we couldn't find that code," double-check what you typed — the codes have hyphens between three parts (like BEAR-MOON-4217). Capitalisation doesn't matter. If the code is right and still isn't found, your school's read-a-thon may not be open yet, so check with the school. If your email never came, check your spam folder; if it's still missing after 5 minutes, ask for another one. And if you lost the sheet entirely, ask your child's teacher — they can print a new one with a fresh code.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/parent-dashboard">Your dashboard</Link>.</li>
        <li><Link href="/help/articles/sharing-with-family">Sharing with family</Link>.</li>
        <li><Link href="/help/articles/logging-minutes-at-home">Logging reading from home</Link>.</li>
      </ul>
    </>
  );
}
